import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert, Card, ListGroup, Badge } from 'react-bootstrap';
import { projectsAPI } from '../services/api';

const ProjectForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    description: '',
    project_date: '',
    cost: '',
    status: 'planning'
  });
  const [files, setFiles] = useState<FileList | null>(null);
  const [existingDocs, setExistingDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    try {
      const response = await projectsAPI.getById(String(id));
      const project = response.data;
      setFormData({
        title: project.title,
        summary: project.summary || '',
        description: project.description || '',
        project_date: project.project_date ? project.project_date.split('T')[0] : '',
        cost: project.cost || '',
        status: project.status
      });
      setExistingDocs(project.documents || []);
    } catch (err) {
      setError('Failed to load project');
    }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });

      if (files) {
        Array.from(files).forEach(file => {
          data.append('documents', file);
        });
      }

      if (isEdit) {
        await projectsAPI.update(String(id), data);
      } else {
        await projectsAPI.create(data);
      }

      navigate('/projects');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    if (window.confirm('Delete this document?')) {
      try {
        await projectsAPI.deleteDocument(String(docId));
        setExistingDocs(existingDocs.filter(doc => doc.id !== docId));
      } catch (err) {
        setError('Failed to delete document');
      }
    }
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '3rem' }}>
      <Container style={{ maxWidth: '900px' }}>
        <div className="mb-4">
          <h1 style={{ 
            color: '#00579a', 
            fontWeight: 600,
            fontSize: '28px',
            marginBottom: '8px'
          }}>
            {isEdit ? 'Edit Project' : 'Create New Project'}
          </h1>
          <p style={{ color: '#8c8c8c', fontSize: '14px', margin: 0 }}>
            {isEdit ? 'Update project information and documents' : 'Fill in the details to add a new project'}
          </p>
        </div>

        {error && (
          <Alert 
            variant="danger" 
            dismissible 
            onClose={() => setError('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '24px'
            }}
          >
            {error}
          </Alert>
        )}

        <Card style={{ 
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          marginBottom: '24px'
        }}>
          <Card.Body style={{ padding: '32px' }}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>
                  Project Title <span style={{ color: '#ff4d4f' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="Enter project title"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Summary</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="summary"
                  value={formData.summary}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Brief project summary"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Detailed project description"
                />
              </Form.Group>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>Project Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="project_date"
                      value={formData.project_date}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>Cost (N$)</Form.Label>
                    <Form.Control
                      type="number"
                      name="cost"
                      value={formData.cost}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  style={{
                    ...inputStyle,
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '16px 12px'
                  }}
                >
                  <option value="planning">Planning</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Upload Documents</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={(e: any) => setFiles(e.target.files)}
                  style={inputStyle}
                />
                <Form.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                  Upload project documents, reports, or images
                </Form.Text>
              </Form.Group>

              {isEdit && existingDocs.length > 0 && (
                <div className="mb-4">
                  <Form.Label style={labelStyle}>Existing Documents</Form.Label>
                  <ListGroup style={{ borderRadius: '6px', overflow: 'hidden' }}>
                    {existingDocs.map(doc => (
                      <ListGroup.Item 
                        key={doc.id} 
                        className="d-flex justify-content-between align-items-center"
                        style={{
                          border: '1px solid #f0f0f0',
                          padding: '12px 16px',
                          backgroundColor: 'white'
                        }}
                      >
                        <div>
                          <span style={{ color: '#262626', fontSize: '14px' }}>{doc.file_name}</span>
                          {doc.file_type && (
                            <Badge 
                              bg="secondary" 
                              className="ms-2"
                              style={{
                                backgroundColor: '#f0f0f0',
                                color: '#595959',
                                fontWeight: 500,
                                padding: '4px 8px',
                                borderRadius: '4px'
                              }}
                            >
                              {doc.file_type}
                            </Badge>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => handleDeleteDocument(doc.id)}
                          style={{
                            backgroundColor: 'white',
                            color: '#ff4d4f',
                            border: '1px solid #ff4d4f',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            fontSize: '13px',
                            fontWeight: 500
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff1f0';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          Delete
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              <div className="d-flex gap-2 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                <Button 
                  type="submit" 
                  disabled={loading}
                  style={{
                    backgroundColor: '#00579a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = '#004a85';
                  }}
                  onMouseOut={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = '#00579a';
                  }}
                >
                  {loading ? 'Saving...' : (isEdit ? 'Update Project' : 'Create Project')}
                </Button>
                <Button 
                  onClick={() => navigate('/projects')}
                  style={{
                    backgroundColor: 'white',
                    color: '#595959',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#00579a';
                    e.currentTarget.style.color = '#00579a';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#d9d9d9';
                    e.currentTarget.style.color = '#595959';
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  marginBottom: '8px'
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px',
  transition: 'all 0.2s ease'
};

export default ProjectForm;